import { useState, useMemo } from "react";
import { useNativeLanguage } from "../contexts/NativeLanguageContext";

export default function LanguagePickerModal() {
  const { pickerOpen, closePicker, code, setCode, languages } = useNativeLanguage();
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const filtered = search
      ? languages.filter(
          (l) =>
            l.name.toLowerCase().includes(search.toLowerCase()) ||
            l.nativeName.toLowerCase().includes(search.toLowerCase()) ||
            l.region.toLowerCase().includes(search.toLowerCase())
        )
      : languages;
    const map = {};
    filtered.forEach((l) => {
      if (!map[l.region]) map[l.region] = [];
      map[l.region].push(l);
    });
    return map;
  }, [search, languages]);

  if (!pickerOpen) return null;

  function pick(c) {
    setCode(c);
    setSearch("");
    closePicker();
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={closePicker}
    >
      <div
        className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-ink-900 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Choose your language</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Pick your mother tongue. We'll explain things in your language while teaching you English.
              </p>
            </div>
            <button
              onClick={closePicker}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-ink-800 transition"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <svg className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              autoFocus
              placeholder="Search 36 languages…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-100 dark:bg-ink-800 text-gray-900 dark:text-white border-0 focus:ring-2 focus:ring-gold-500 focus:outline-none"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {Object.keys(grouped).length === 0 && (
            <div className="text-center py-12 text-gray-500">No languages match "{search}"</div>
          )}
          {Object.entries(grouped).map(([region, langs]) => (
            <div key={region} className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-2">
                {region}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => pick(l.code)}
                    className={`flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                      code === l.code
                        ? "bg-gradient-to-br from-ink-900 to-gold-600 text-white shadow-lg scale-[1.02]"
                        : "bg-gray-50 dark:bg-ink-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                    }`}
                  >
                    <span className="text-2xl shrink-0">{l.flag}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate text-sm">{l.nativeName}</div>
                      <div className={`text-xs truncate ${code === l.code ? "text-paper" : "text-gray-500 dark:text-gray-400"}`}>
                        {l.name}
                      </div>
                    </div>
                    {code === l.code && (
                      <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-ink-800/50">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            🎯 You're learning <span className="font-semibold text-gold-700">English</span> · Mother tongue helps with explanations
          </p>
        </div>
      </div>
    </div>
  );
}
